import { useContext, useEffect, useRef, useState } from 'react'
import { classes, keyframes, style, stylesheet } from 'typestyle'
import { Link, useNavigate } from 'react-router-dom'
import moment from 'moment'

/* Context ====================================================================================== */
import { NavigationContext } from '../../__aeki__/contexts/navigation'
import { withRouter } from '../../__aeki__/contexts/with-router'

/* Core ========================================================================================= */
import { addSnippet } from '../../__aeki__/core/snippet-manager'

/* Hooks ======================================================================================== */
import useFetch from '../../__aeki__/hooks/use-fetch'
import useValue from '../../__aeki__/hooks/use-value'
import useCounter from '../../__aeki__/hooks/use-counter'

/* Components =================================================================================== */
import { Box } from '../../__aeki__/components/box/box.component'
import { Button } from '../../__aeki__/components/button'
import { Line } from '../../__aeki__/components/line'
import { Text } from '../../__aeki__/components/text/text.component'
import { getCurrentUser } from '../../__aeki__/services/auth'
import Skeleton from '../../__aeki__/components/skeleton'

/* Styles ======================================================================================= */
import { mb4 } from '../../__aeki__/styles/styleset/margin/mb4'
import { mb8 } from '../../__aeki__/styles/styleset/margin/mb8'
import { p16 } from '../../__aeki__/styles/styleset/padding/p16'
import { pb8i } from '../../__aeki__/styles/styleset/padding/pb8i'
import { textCenteri } from '../../__aeki__/styles/styleset/text-center/textCenteri'
import { pt8i } from '../../__aeki__/styles/styleset/padding/pt8i'
import { pb12i } from '../../__aeki__/styles/styleset/padding/pb12i'
import { pt12i } from '../../__aeki__/styles/styleset/padding/pt12i'
import { ml4 } from '../../__aeki__/styles/styleset/margin/ml4'
import { mt16 } from '../../__aeki__/styles/styleset/margin/mt16'
import { DailyInfoSpit } from '../calendar'
import { createMediaQuery } from '../../__aeki__/components/style-class/style-class.utils'
import { textRightmd } from '../../__aeki__/styles/styleset/text-right/textRightmd'
import { Snippet } from '../../__aeki__/modules/layouts/snippet'
import { pt0 } from '../../__aeki__/styles/styleset/padding/pt0'
import { pt0i } from '../../__aeki__/styles/styleset/padding/pt0i'
import { Circle } from '../../__aeki__/components/circle'
import { paddingLeftInner } from '../../__aeki__/styles/default/padding'
import { pb16 } from '../../__aeki__/styles/styleset/padding/pb16'
import { pt16 } from '../../__aeki__/styles/styleset/padding/pt16'
import { p4 } from '../../__aeki__/styles/styleset/padding/p4'
import { pt12 } from '../../__aeki__/styles/styleset/padding/pt12'
import { pb12 } from '../../__aeki__/styles/styleset/padding/pb12'

export const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

export const TakeAttendance = ({ groupId, rowId, index, refDate }) => {
  const apiUrl = `${process.env.REACT_APP_API_URL}/a/snippets/calendar/${rowId}/${index}`
  const postUrl = `${process.env.REACT_APP_API_URL}/a/snippets/calendar/${groupId}/${rowId}/add`

  const [data, $data] = useFetch({ parsed: {} }, { url: apiUrl })
  const [post, $post] = useFetch({ parsed: {} }, { url: postUrl })
  const history = useNavigate()

  useEffect(() => {
    if (data.mounted) {
      $data.get({
        url: `${apiUrl}`,
      })
    }
  }, [data.mounted])

  return (
    <Button
      onClick={() => {
        if (!post.meta.isLoading) {
          if (data.payload.parsed[moment(refDate).format('MMM, DD, YYYY')]) {
            history(
              `/a/groups/${groupId}/base/childcare-classes-dailies/table/${data.payload.parsed[moment(refDate).format('MMM, DD, YYYY')].id
              }`,
            )
          } else {
            $post
              .post({
                body: {
                  body: {
                    date: refDate.toISOString(),
                  },
                },
              })
              .then((payload: any) => {
                history(`/a/groups/${groupId}/base/childcare-classes-dailies/table/${payload.id}`)
              })
          }
        }
      }}
      size="md"
      label={'Go to Attendance'}
      baseline={'primary'}
      square
    />
  )
}

enum STATUS {
  NOT_STARTED = 'Not Started',
  IN_PROGRESS = 'In Progress',
  FINISHED = 'Finished',
}

const TODAY = new Date()
const REF_DAY = TODAY.getDay()
const TODAY_MOMENT = moment(TODAY)

const STATUS_KEYFRAME = keyframes({
  '0%': { opacity: 0.7 },
  '50%': { opacity: 1 },
  '100%': { opacity: 0.7 },
})

export const Classes = ({
  groupId,
  program,
  day,
  refDate,
}: {
  groupId: string
  program: any
  day?: any
  refDate?: any
}) => {
  const apiUrl = `${process.env.REACT_APP_API_URL}/a/snippets/childcare-actions/${groupId}/${program.id}`

  const [data, $data] = useFetch({ data: [], count: 0 }, { url: apiUrl })
  const [classList, $classList] = useValue({ data: [] })
  const history = useNavigate()

  useEffect(() => {
    if (data.mounted) {
      $data
        .get({
          url: `${apiUrl}`,
        })
        .then((payload: any) => {
          $classList.set(payload.classes)
        })
    }
  }, [data.mounted])

  return (
    <>
      {classList.data.map((item: any, key: any) => {
        const startTime = moment(
          moment(refDate).format('MM, DD, YYYY') + ' ' + program?.body?.hours?.[day]?.open,
        )
        const endTime = moment(
          moment(refDate).format('MM, DD, YYYY') + ' ' + program?.body?.hours?.[day]?.close,
        )
        let status

        if (TODAY_MOMENT.isBefore(startTime)) {
          status = STATUS.NOT_STARTED
        } else if (TODAY_MOMENT.isAfter(endTime)) {
          status = STATUS.FINISHED
        } else if (TODAY_MOMENT.isBetween(startTime, endTime)) {
          status = STATUS.IN_PROGRESS
        }

        return (
          <Box
            key={key}
            className={classes(mb8)}
            baseline="none"
            rounded
            row
            style={{ width: 'calc(100%)', overflow: 'hidden', userSelect: 'none' }}
            relative
            shadow
          >
            <Box baseline="surface" rounded row>
              <Box baseline="none" row className={classes(p16, pt8i, pb8i)}>
                <Text size="sm" className={mb4} shrink baseline="surface" lighter>
                  Class
                </Text>
                <Text size="md" semiBold>
                  {item.body.title}
                </Text>
              </Box>
              <Line />
              <Box flex alignCenterY spaceBetween fullWidth baseline="none">
                <Box
                  className={style({
                    ...createMediaQuery.md({ justifyContent: 'space-between' }),
                    // justifyContent: 'space-between',
                  })}
                  flex
                  fullWidth
                >
                  <Box flex baseline="none">
                    <Box baseline="none" row className={classes(p16, pt12i, pb12i)}>
                      <Text size="sm" className={mb4} shrink baseline="surface" lighter>
                        Hours
                      </Text>
                      <Box baseline="none" flex alignCenterY>
                        <Text size="md">{`${program?.body?.hours?.[day]?.open} - ${program?.body?.hours?.[day]?.close}`}</Text>
                        <div
                          style={{
                            background:
                              status === STATUS.IN_PROGRESS ? '#4480f4' : 'rgb(240,240,240)',
                            borderRadius: 2,
                            marginLeft: 8,
                            color: status === STATUS.IN_PROGRESS ? 'white' : 'rgb(120,120,120)',
                            fontSize: 11,
                            padding: '2px 4px',
                            height: 'fit-content',
                          }}
                          className={style({
                            animationDuration: '2s',
                            animationIterationCount: 'infinite',
                            animationName: STATUS_KEYFRAME,
                          })}
                        >
                          {status}
                        </div>
                      </Box>
                    </Box>
                    {/* <Line /> */}
                    <Box baseline="none" row className={classes(p16, pt12i, pb12i)}>
                      <Text size="sm" className={mb4} shrink baseline="surface" lighter>
                        Teacher
                      </Text>
                      <Box baseline="none" flex>
                        <Text size="md">{item.body.teacher}</Text>
                      </Box>
                    </Box>
                  </Box>
                  <Box baseline="none" row className={classes(p16, pt12i, pb12i)}>
                    <Text size="sm" className={mb4} shrink baseline="surface" lighter>
                      Students
                    </Text>
                    <Box baseline="none" row>
                      <Text size="md" className={textRightmd}>
                        <DailyInfoSpit
                          groupId={groupId}
                          rowId={item.id}
                          refDate={moment(refDate).toISOString()}
                        >
                          {temp => {
                            if (Object.keys(temp.payload).length > 0) {
                              return `${temp?.payload?.body?.attendance?.attendance?.filter(
                                a => a.in && !a.absent,
                              ).length || 0} / ${temp.payload?.body?.attendance?.attendance
                                ?.length ||
                              temp.payload?.body?.students?.length ||
                              0}`
                            } else {
                              return `0 / ${item.body.students?.length || 0}`
                            }
                          }}
                        </DailyInfoSpit>
                      </Text>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Line />
              <Box className={classes(p16, pt8i, pb8i)} baseline="none" flex style={{ gap: 12 }}>
                <TakeAttendance
                  groupId={groupId}
                  rowId={item.id}
                  index={DAYS.indexOf(day)}
                  refDate={refDate}
                />
                <Button
                  size="md"
                  baseline="surface"
                  label="View / Edit Class"
                  square
                  onClick={() => {
                    history(`/a/groups/${groupId}/base/childcare-classes/table/${item.id}`)
                  }}
                />
              </Box>
            </Box>
          </Box>
        )
      })}
    </>
  )
}

type CalendarDate = {
  date: Date
  isToday?: boolean
  isWeekend?: boolean
  isNotCurrentMonth?: boolean
}

const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const weekdaysMap = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
}
const weekdaysMapReverse = {
  0: 'sun',
  1: 'mon',
  2: 'tue',
  3: 'wed',
  4: 'thu',
  5: 'fri',
  6: 'sat',
}

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export default addSnippet(
  'ChildcareActions',
  withRouter((props: any) => {
    const { title, params = {} } = props
    const { groupId } = props?.match.params
    const { currentUser } = getCurrentUser()

    const [day, $day] = useCounter(REF_DAY, { min: -Infinity, max: Infinity })
    const refDate = new Date(TODAY)
    refDate.setDate(TODAY.getDate() + (day - REF_DAY))
    const firstDateOfMonth = moment(refDate).startOf('month').toDate()
    const lastDateOfMonth = moment(refDate).endOf('month').toDate()

    const apiUrl = `${process.env.REACT_APP_API_URL}/a/snippets/childcare-actions/${groupId}`
    const schedulesUrl = `${process.env.REACT_APP_API_URL}/a/snippets/childcare-schedules/${groupId}/date/${firstDateOfMonth}/${lastDateOfMonth}`

    const [data, $data] = useFetch({ data: [], count: 0 }, { url: apiUrl })
    const [schedules, $schedules] = useFetch({ data: [], parsed: {}, count: 0 }, { url: schedulesUrl })
    const [programs, $programs] = useValue([])
    const [classList, $classList] = useValue([])
    const [todayClassList, $todayClassList] = useValue([])
    const [weekly, $weekly] = useValue([[], [], [], [], [], [], []])
    const [selectedDate, setSelectedDate] = useState(new Date())

    useEffect(() => {
      if (data.mounted) {
        $data
          .get({
            url: `${apiUrl}`,
          })
          .then((payload: any) => {
            const tempWeekly: any[][] = [[], [], [], [], [], [], []]

            $programs.set(payload.programs.data)
            $classList.set(
              payload.classes.data.map(item => {
                if (item?.body?.hours) {
                  if (!item.body.hours.customHours) {
                    item.body.hours = {
                      ...(programs.find(a => a.id === item.parentRow[0])?.body?.hours || {}),
                      ...item.body.hours,
                    }
                  }

                  for (const key in item.body.hours) {
                    if (tempWeekly[weekdaysMap[key]]) tempWeekly[weekdaysMap[key]].push(item)
                  }
                }

                return item
              }),
            )

            $weekly.set(tempWeekly)
          })
      }
    }, [data.mounted, day])

    useEffect(() => {
      if (schedules.mounted) {
        $schedules
          .get({
            url: `${schedulesUrl}`,
          })
          .then((payload: any) => {
            console.log(payload)
          })
      }
    }, [schedules.mounted, day])

    const temp = useRef<any>([])
    function getCalendarMonth(month, year) {
      const weeks: CalendarDate[][] = []
      const daysInMonth = new Date(year, month + 1, 0).getDate()
      const firstDay = new Date(year, month, 1).getDay()
      const lastDay = new Date(year, month + 1, 0).getDay()
      let week: CalendarDate[] = []

      temp.current = []

      // Add dates from previous month to first week
      if (firstDay > 0) {
        const prevMonth = month === 0 ? 11 : month - 1
        const prevYear = month === 0 ? year - 1 : year
        const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate()

        for (let i = firstDay - 1; i >= 0; i--) {
          const prevMonthDate = new Date(prevYear, prevMonth, daysInPrevMonth - i)
          const isToday =
            TODAY.getFullYear() === prevMonthDate.getFullYear() &&
            TODAY.getMonth() === prevMonthDate.getMonth() &&
            TODAY.getDate() === prevMonthDate.getDate()

          // if (
          //   isToday &&
          //   prevMonthDate.getMonth() == TODAY.getMonth() &&
          //   prevMonthDate.getFullYear() === TODAY.getFullYear()
          // )
          //   temp.current.push(true)

          week.push({ date: prevMonthDate, isNotCurrentMonth: true, isToday })
        }
      }

      // Add dates from current month
      for (let i = 1; i <= daysInMonth; i++) {
        const currMonthDate = new Date(year, month, i)
        const isToday =
          TODAY.getFullYear() === currMonthDate.getFullYear() &&
          TODAY.getMonth() === currMonthDate.getMonth() &&
          TODAY.getDate() === currMonthDate.getDate()
        week.push({ date: currMonthDate, isToday })

        if (
          isToday &&
          currMonthDate.getMonth() == TODAY.getMonth() &&
          currMonthDate.getFullYear() === TODAY.getFullYear()
        )
          temp.current.push(true)

        // If the current day is a Saturday, add the week to the array of weeks and start a new week
        if (currMonthDate.getDay() === 6) {
          weeks.push(week)
          week = []
        }
      }

      // Add dates from next month to last week
      if (lastDay < 6) {
        const nextMonth = month === 11 ? 0 : month + 1
        const nextYear = month === 11 ? year + 1 : year

        for (let i = 1; i <= 6 - lastDay; i++) {
          const nextMonthDate = new Date(nextYear, nextMonth, i)
          const isToday =
            TODAY.getFullYear() === nextMonthDate.getFullYear() &&
            TODAY.getMonth() === nextMonthDate.getMonth() &&
            TODAY.getDate() === nextMonthDate.getDate()

          // if (
          //   isToday &&
          //   nextMonthDate.getMonth() == TODAY.getMonth() &&
          //   nextMonthDate.getFullYear() === TODAY.getFullYear()
          // )
          //   temp.current.push(true)

          week.push({ date: nextMonthDate, isNotCurrentMonth: true, isToday })
        }
      }

      weeks.push(week)
      return weeks
    }

    const renderWeeks = () => {
      return weekdays.map(week => {
        return (
          <Box className={STYLES.week} key={week} flex alignCenterX>
            <Text baseline="surface" lighter size="xs">
              {week}
            </Text>
          </Box>
        )
      })
    }

    const renderCalendar = () => {
      const weeks = getCalendarMonth(selectedDate.getMonth(), selectedDate.getFullYear())

      return weeks.map((week, key1) => {
        return week.map((day, key2) => {
          const items = weekly[key2].sort((a, b) => {
            const aTime = moment(a.body.hours[weekdaysMapReverse[key2]].open, 'h:mm A')
            const bTime = moment(b.body.hours[weekdaysMapReverse[key2]].open, 'h:mm A')
            return bTime.diff(aTime)
          })

          const scheduleItems = schedules.payload.parsed[day.date.getDate()]

          return (
            <div className={STYLES.date} key={`${key1}-${key2}`}>
              <Box className={p16} flex alignCenterX>
                <Circle size="sm" baseline={day.isToday ? 'primary' : 'none'}>
                  <Text
                    size="xs"
                    shrink
                    baseline={day.isToday ? 'primary' : 'surface'}
                    lighter={day.isNotCurrentMonth}
                    style={{ marginTop: 1 }}
                  >
                    {day.date.getDate()}
                  </Text>
                </Circle>
              </Box>
              <Box row className={classes(p16, pt0i)} style={{ gap: 12 }}>
                {
                  scheduleItems?.map((item, key3) => {
                    console.log(item)
                    return <Text size="xs" disableWrap baseline="surface" link style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', textDecoration: item.body.status === "Cancelled" ? 'line-through' : '' }} key={key3}>
                      <Link to={
                        `/a/groups/${groupId}/base/childcare-waitlist/table/${item.body.parent}`
                      }>
                        {`${moment(item.body.timeStart, 'HH:mm').format('hh:mm A')} - ${item.body.title}`}
                      </Link>
                    </Text>
                  })
                }
                {/* {items.map((item, key3) => {
                  return (
                    <Box style={{ gap: 0 }} key={key3} row>
                      <Text size="xs" disableWrap baseline="surface">
                        {item.body.hours[weekdaysMapReverse[key2]].open}
                      </Text>
                      <Link to={`/a/groups/${groupId}/base/childcare-classes/table/${item.id}`}>
                        <Text size="xs" link>
                          {item.body.generalInformation.name || 'No Name'}
                        </Text>
                      </Link>
                    </Box>
                  )
                })} */}
              </Box>
            </div>
          )
        })
      })
    }

    return (
      <>
        {/* <Box
          rounded
          baseline="surface"
          className={classes(
            style({
              marginBottom: 16,
              padding: '16px 16px',
              overflow: 'hidden',
              height: 64,
            }),
          )}
          // border
          shadow
          flex
          alignCenterY
          relative
        >
          <Box
            style={{
              left: 0,
              top: 0,
              width: 4,
              background: '#266ffb',
              height: '100%',
              position: 'absolute',
            }}
          />
          <Box baseline="none" flex spaceBetween alignCenterY fullWidth>
            <Box baseline="none" row>
              <Text baseline="surface" size="sm" style={{ marginBottom: 4 }} shrink>
                Insight
              </Text>
              {data.meta.isLoading ? (
                <Skeleton width={200} height={22.44} />
              ) : (
                <Text baseline="surface" size="lg">
                  {(() => {
                    const momentDate = moment(refDate)
                    if (!data.meta.isLoading) {
                      if (programs.length == 0) {
                        return (
                          <>
                            No classes{' '}
                            {momentDate.isSame(moment(TODAY))
                              ? `today, ${momentDate.format('MMM, DD, YYYY')}`
                              : `on ${momentDate.format('dddd')}, ${momentDate.format(
                                  'MMM, DD, YYYY',
                                )}`}
                          </>
                        )
                      } else {
                        return (
                          <>
                            {` ${temp.current.length > 1 ? 'There are' : 'There is'} `}
                            <b>{temp.current.length}</b>
                            {` ${temp.current.length > 1 ? 'classes' : 'class'} `}
                            {momentDate.isSame(moment(TODAY))
                              ? `today, ${momentDate.format('MMM, DD, YYYY')}`
                              : `on ${momentDate.format('dddd')}, ${momentDate.format(
                                  'MMM, DD, YYYY',
                                )}`}
                          </>
                        )
                      }
                    }
                  })()}
                </Text>
              )}
            </Box>
          </Box>
        </Box> */}
        <Snippet className={mt16}>
          <Box
            rounded
            className={classes(pt12, pb12, paddingLeftInner)}
            flex
            spaceBetween
            alignCenterY
          >
            <Text size="xl" semiBold>
              Calendar | {months[selectedDate.getMonth()]} {selectedDate.getFullYear()}
            </Text>
            <Box flex style={{ gap: 8 }}>
              <Button
                size="sm"
                baseline="background"
                label="Today"
                square
                onClick={() => {
                  setSelectedDate(prev => {
                    const newDate = new Date()
                    newDate.setMonth(newDate.getMonth())

                    return newDate
                  })
                }}
              />
              <Box className={p4} row>
                <Line horiz />
              </Box>
              <Button
                size="sm"
                icon={<i className="material-icons-two-tone">keyboard_arrow_left</i>}
                onClick={() => {
                  setSelectedDate(prev => {
                    const newDate = new Date(prev)
                    newDate.setMonth(newDate.getMonth() - 1)

                    return newDate
                  })
                }}
              />
              <Button
                size="sm"
                icon={<i className="material-icons-two-tone">keyboard_arrow_right</i>}
                onClick={() => {
                  setSelectedDate(prev => {
                    const newDate = new Date(prev)
                    newDate.setMonth(newDate.getMonth() + 1)

                    return newDate
                  })
                }}
              />
            </Box>
          </Box>
          <Line />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr',
              gridAutoRows: '1fr',
              gridTemplateRows: 20,
              gap: '0px 0px',
            }}
          >
            {renderWeeks()}
            {renderCalendar()}
          </div>
        </Snippet>
      </>
    )
  }),
)

const STYLES = stylesheet({
  week: {
    borderRight: '1px solid rgb(240,240,240)',
    // borderBottom: '1px solid rgb(240,240,240)',
    userSelect: 'none',
    padding: '12px 16px 0px 16px',
  },
  date: {
    minHeight: 180,
    borderRight: '1px solid rgb(240,240,240)',
    borderBottom: '1px solid rgb(240,240,240)',
    userSelect: 'none',
    minWidth: 0,
  },
})
